import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  computed,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { AuthService } from '../../../core/services/auth.service';
import { ThemeService } from '../../../core/services/theme.service';
import { LoginService } from '../../../features/login/services/login.service';
import { AppSettingsService } from '../../../core/services/app-settings.service';

import { WareneingaengeService } from '../../../features/wareneingaenge/services/wareneingaenge.service';
import { BestellungenService } from '../../../features/bestellungen/services/bestellungen.service';
import { ReturnsService } from '../../../features/returns/services/returns.service';
import { InventoryService } from '../../../features/inventory/services/inventory.service';
import { EntlastungenService } from '../../../features/entlastungen/services/entlastungen.service';

interface SidebarNavItem {
  label: string;
  route: string;
  icon: string;
  badge?: number;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    RouterLinkActive,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly loginService = inject(LoginService);
  private readonly bestellungenService = inject(BestellungenService);
  private readonly wareneingaengeService = inject(WareneingaengeService);
  private readonly returnsService = inject(ReturnsService);
  private readonly inventoryService = inject(InventoryService);
  private readonly entlastungenService = inject(EntlastungenService);
  protected readonly appSettingsService = inject(AppSettingsService);
  protected readonly themeService = inject(ThemeService);

  @Input() isCollapsed = false;
  @Input() isMobile = false;

  @Output() collapsedToggle = new EventEmitter<void>();
  @Output() mobileClose = new EventEmitter<void>();

  protected readonly userName = this.authService.getLoggedInUserName();
  protected readonly bestellungenOffenCount =
    this.bestellungenService
      .offeneBestellungenAnzahl;
  protected readonly wareneingaengeAnzahlOffen =
    this.wareneingaengeService.anzahlOffen;
  protected readonly returnsAnzahlOffen =
    this.returnsService.anzahlOffen;
  protected readonly inventoryAnzahlOffen =
    this.inventoryService.anzahlOffen;
  protected readonly entlastungenAnzahlOffen =
    this.entlastungenService.anzahlOffen;

  protected readonly navItems = computed<SidebarNavItem[]>(() => {
    if (this.authService.isCopagoAdmin()) {
      return [
        {
          label: 'Benutzerverwaltung',
          route: '/benutzerverwaltung',
          icon: 'manage_accounts',
        },
      ];
    }
    const items: SidebarNavItem[] = [];

    if (this.authService.canAccessModule('Bestellungen')) {
      items.push({
        label: 'Bestellungen',
        route: '/bestellungen',
        icon: 'shopping_cart',
        badge: this.bestellungenOffenCount() ?? undefined,
      });
    }

    if (this.authService.canAccessModule('Wareneingaenge')) {
      items.push({
        label: 'Wareneingänge',
        route: '/wareneingaenge',
        icon: 'inventory_2',
        badge: this.wareneingaengeAnzahlOffen(),
      });
    }

    if (this.authService.canAccessModule('Retouren')) {
      items.push({
        label: 'Retouren',
        route: '/retouren',
        icon: 'rotate_left',
        badge: this.returnsAnzahlOffen(),
      });
    }

    if (this.authService.canAccessModule('Entlastungen')) {
      items.push({
        label: 'Entlastungen',
        route: '/entlastungen',
        icon: 'inventory',
        badge: this.entlastungenAnzahlOffen(),
      });
    }

    if (this.authService.canAccessModule('Inventuren')) {
      items.push({
        label: 'Inventur',
        route: '/inventur',
        icon: 'assignment',
        badge: this.inventoryAnzahlOffen(),
      });
    }

    if (this.authService.canSeeBenutzerverwaltung()) {
      items.push({
        label: 'Benutzerverwaltung',
        route: '/benutzerverwaltung',
        icon: 'manage_accounts',
      });
    }

    return items;
  });

  constructor() {
    this.loadBestellungenOffenCount();
  }

  private loadBestellungenOffenCount(): void {
    if (
      this.authService.isCopagoAdmin() ||
      !this.authService.canAccessModule(
        'Bestellungen'
      )
    ) {
      return;
    }

    this.bestellungenService
      .getOffeneBestellungenAnzahl()
      .subscribe({
        error: () => {
          // Der vorhandene Badge-Wert bleibt erhalten.
        },
      });
  }

  ngOnInit(): void {
    if (this.authService.canAccessModule('Wareneingaenge')) {
      this.wareneingaengeService.getAnzahlOffen().subscribe({
        error: () => {
          // Badge bleibt bei einem Fehler ausgeblendet.
        },
      });
    }

    if (this.authService.canAccessModule('Retouren')) {
      this.returnsService.getAnzahlOffen().subscribe({
        error: () => {
          // Badge bleibt bei einem Fehler ausgeblendet.
        },
      });
    }

    if (this.authService.canAccessModule('Entlastungen')) {
      this.entlastungenService.getAnzahlOffen().subscribe({
        error: () => {
          // Badge bleibt bei einem Fehler ausgeblendet.
        },
      });
    }

    if (this.authService.canAccessModule('Inventuren')) {
      this.inventoryService.getAnzahlOffen().subscribe({
        error: () => {
          // Badge bleibt bei einem Fehler ausgeblendet.
        },
      });
    }
  }

  protected onToggleCollapsed(): void {
    this.collapsedToggle.emit();
  }

  protected onNavigate(): void {
    if (this.isMobile) {
      this.mobileClose.emit();
    }
  }

  protected logout(): void {
    const refreshToken = this.authService.getRefreshToken();

    if (!refreshToken) {
      this.authService.clearSession();
      this.router.navigate(['/login']);
      return;
    }

    this.loginService.logout(refreshToken).subscribe({
      next: () => {
        this.authService.clearSession();
        this.router.navigate(['/login']);
      },
      error: () => {
        this.authService.clearSession();
        this.router.navigate(['/login']);
      },
    });
  }
}